import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowRight, FaLayerGroup } from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  StatusPill,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { useAuth } from '../../contexts/AuthContext';
import { TEAMS, canSee } from '../../config/teams';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1rem;
`;

const TeamCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-top: 2px solid ${({ $color }) => $color || accent};
  border-radius: 8px;
  overflow: hidden;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.1rem 0.6rem;

  .titles { min-width: 0; }

  h3 {
    margin: 0;
    font-size: 1.05rem;
    color: var(--text-primary);
    line-height: 1.15;
  }

  .tag {
    display: block;
    margin-top: 3px;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const IconTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 8px;
  font-size: 1.05rem;
  color: ${({ $color }) => $color || accent};
  background: ${({ $color }) => ($color ? `${$color}1a` : 'rgba(0,180,216,0.1)')};
  border: 1px solid ${({ $color }) => ($color ? `${$color}55` : 'rgba(0,180,216,0.3)')};
`;

const Role = styled.p`
  margin: 0;
  padding: 0 1.1rem 0.85rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--text-secondary);
`;

const ToolList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${borderSoft};
`;

const ToolLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 1.1rem;
  text-decoration: none;
  border-bottom: 1px solid ${borderSoft};
  transition: background 0.15s ease;

  &:last-child { border-bottom: none; }
  &:hover { background: rgba(255, 255, 255, 0.03); }
  &:hover .go { opacity: 1; transform: translateX(0); }

  .ico {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 6px;
    font-size: 0.72rem;
    color: ${({ $color }) => $color || accent};
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid ${borderSoft};
  }

  .body { flex: 1; min-width: 0; }

  .label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.88rem;
    color: var(--text-primary);
    font-weight: 500;
  }

  .desc {
    display: block;
    font-size: 0.74rem;
    color: var(--text-disabled);
    margin-top: 1px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .badge {
    font-family: ${mono};
    font-size: 0.54rem;
    letter-spacing: 0.14em;
    color: ${accent};
    background: rgba(0, 180, 216, 0.1);
    border: 1px solid rgba(0, 180, 216, 0.3);
    padding: 1px 5px;
    border-radius: 3px;
  }

  .go {
    color: ${({ $color }) => $color || accent};
    font-size: 0.7rem;
    opacity: 0;
    transform: translateX(-4px);
    transition: opacity 0.15s ease, transform 0.15s ease;
    flex-shrink: 0;
  }
`;

const Equipos = () => {
  const { user } = useAuth();
  const role = user?.role || 'admin';

  const teams = useMemo(
    () =>
      TEAMS.map((t) => ({ ...t, tools: t.tools.filter((tool) => canSee(tool.to, role)) }))
        .filter((t) => t.tools.length > 0),
    [role]
  );

  const totalTools = teams.reduce((acc, t) => acc + t.tools.length, 0);

  return (
    <TechShell>
      <TechPageHeader
        title="Equipos"
        subtitle="Tu operación política dividida en equipos. Cada módulo reúne las herramientas de un área — entrá a la tuya."
        dossier="OPS://EQUIPOS"
      >
        <StatusPill>
          <FaLayerGroup style={{ marginRight: 5 }} /> <strong>{teams.length}</strong> equipos
        </StatusPill>
        <StatusPill>
          <strong>{totalTools}</strong> herramientas
        </StatusPill>
      </TechPageHeader>

      <Grid>
        {teams.map((team) => {
          const TeamIcon = team.icon;
          return (
            <TeamCard key={team.id} $color={team.color}>
              <CardHead>
                <IconTile $color={team.color}><TeamIcon /></IconTile>
                <div className="titles">
                  <h3>{team.name}</h3>
                  <span className="tag">{team.tagline} · {team.tools.length} herramientas</span>
                </div>
              </CardHead>
              <Role>{team.role}</Role>
              <ToolList>
                {team.tools.map((tool) => {
                  const ToolIcon = tool.icon;
                  return (
                    <ToolLink key={tool.to} to={tool.to} $color={team.color}>
                      <span className="ico"><ToolIcon /></span>
                      <span className="body">
                        <span className="label">
                          {tool.label}
                          {tool.badge && <span className="badge">{tool.badge}</span>}
                        </span>
                        <span className="desc">{tool.desc}</span>
                      </span>
                      <FaArrowRight className="go" />
                    </ToolLink>
                  );
                })}
              </ToolList>
            </TeamCard>
          );
        })}
      </Grid>
    </TechShell>
  );
};

export default Equipos;
